<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguageLabel,
} from 'dashboard/constants/translationLanguages';
import { useConversationTranslation } from 'dashboard/composables/useConversationTranslation';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const showPanel = ref(false);
const conversationIdRef = computed(() => props.conversationId);

const {
  settings,
  updateSettings,
} = useConversationTranslation(conversationIdRef);

const togglePanel = () => {
  showPanel.value = !showPanel.value;
};

const closePanel = () => {
  showPanel.value = false;
};

const handleEnabledChange = value => {
  updateSettings({ enabled: value });
  if (value) {
    showPanel.value = true;
  } else {
    showPanel.value = false;
  }
};
</script>

<template>
  <div
    v-on-click-outside="closePanel"
    class="relative flex items-center gap-2"
  >
    <div class="flex items-center gap-2 px-2 py-1 rounded-lg bg-n-alpha-1">
      <span class="text-sm text-n-slate-12 whitespace-nowrap">
        {{ t('CONVERSATION.TRANSLATION.TITLE') }}
      </span>
      <Switch
        :model-value="settings.enabled"
        @update:model-value="handleEnabledChange"
      />
      <button
        type="button"
        class="flex items-center justify-center text-n-slate-11 hover:text-n-slate-12"
        @click="togglePanel"
      >
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 transition-transform"
          :class="{ 'rotate-180': showPanel }"
        />
      </button>
    </div>

    <div
      v-if="showPanel && settings.enabled"
      class="absolute top-full ltr:right-0 rtl:left-0 mt-2 z-50 w-80 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg p-4"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.TRANSLATION.CONTACT_LANGUAGE') }}
          </label>
          <select
            :value="settings.contactLanguage"
            class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 outline-none focus:border-n-brand"
            @change="
              updateSettings({ contactLanguage: $event.target.value })
            "
          >
            <option
              v-for="language in TRANSLATION_LANGUAGES"
              :key="language.id"
              :value="language.id"
            >
              {{ language.label }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.TRANSLATION.AGENT_LANGUAGE') }}
          </label>
          <select
            :value="settings.agentLanguage"
            class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 outline-none focus:border-n-brand"
            @change="updateSettings({ agentLanguage: $event.target.value })"
          >
            <option
              v-for="language in TRANSLATION_LANGUAGES"
              :key="language.id"
              :value="language.id"
            >
              {{ language.label }}
            </option>
          </select>
        </div>

        <div class="h-px bg-n-weak" />

        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION.TRANSLATION.SEND_TRANSLATION') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('CONVERSATION.TRANSLATION.SEND_TRANSLATION_HELP') }}
            </span>
          </div>
          <Switch
            :model-value="settings.sendTranslation"
            @update:model-value="updateSettings({ sendTranslation: $event })"
          />
        </div>

        <label
          v-if="settings.sendTranslation"
          class="flex items-center gap-2 cursor-pointer"
        >
          <input
            type="checkbox"
            class="rounded border-n-weak text-n-brand focus:ring-n-brand"
            :checked="settings.sendPreview"
            @change="
              updateSettings({ sendPreview: $event.target.checked })
            "
          />
          <span class="text-sm text-n-slate-12">
            {{ t('CONVERSATION.TRANSLATION.SEND_PREVIEW') }}
          </span>
        </label>

        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION.TRANSLATION.RECEIVE_TRANSLATION') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('CONVERSATION.TRANSLATION.RECEIVE_TRANSLATION_HELP') }}
            </span>
          </div>
          <Switch
            :model-value="settings.receiveTranslation"
            @update:model-value="
              updateSettings({ receiveTranslation: $event })
            "
          />
        </div>

        <div class="pt-2 border-t border-n-weak">
          <p class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.TRANSLATION.PROVIDER') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
